import { useCallback, useEffect, useRef, useState } from "react";
import { MapPin, Loader2, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBrand } from "@/contexts/BrandContext";
import { useCustomer } from "@/hooks/useCustomer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import type { AddressData } from "@/services/defaultSession";

const GOOGLE_MAPS_API_KEY = "AIzaSyCUM11R3zDdCrA6Ijlnys02Be_9IXsulRE";
const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 };

let mapsLoaded = false;
let mapsLoadPromise: Promise<void> | null = null;
function loadGoogleMaps(): Promise<void> {
  if (mapsLoaded && (window as any).google?.maps) return Promise.resolve();
  if (mapsLoadPromise) return mapsLoadPromise;
  mapsLoadPromise = new Promise((resolve, reject) => {
    const callbackName = `__gmapsInit_${Date.now()}`;
    (window as any)[callbackName] = () => {
      mapsLoaded = true;
      delete (window as any)[callbackName];
      resolve();
    };
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = (e) => {
      mapsLoadPromise = null;
      delete (window as any)[callbackName];
      reject(e);
    };
    document.head.appendChild(script);
  });
  return mapsLoadPromise;
}

interface AddAddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (address: AddressData) => void;
  /** If provided, pre-fills for editing */
  editAddress?: AddressData | null;
  editIndex?: number;
}

interface GeoResult {
  formatted_address: string;
  street: string;
  building: string;
  area: string;
  city: string;
  lat: number;
  lng: number;
}

export default function AddAddressDialog({
  open,
  onOpenChange,
  onSave,
  editAddress,
}: AddAddressDialogProps) {
  const { language } = useLanguage();
  const { brand } = useBrand();
  const { customer } = useCustomer();
  const isAr = language === "ar";

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const [geoResult, setGeoResult] = useState<GeoResult | null>(null);

  // Form fields
  const [streetName, setStreetName] = useState("");
  const [buildingNumber, setBuildingNumber] = useState("");
  const [floorNumber, setFloorNumber] = useState("");
  const [apartmentNumber, setApartmentNumber] = useState("");
  const [details, setDetails] = useState("");
  const [coords, setCoords] = useState<{ lat: number; lng: number }>(DEFAULT_CENTER);

  // Pre-fill when editing
  useEffect(() => {
    if (open && editAddress) {
      setStreetName(editAddress.streetName || "");
      setBuildingNumber(editAddress.buildingNumber || "");
      setFloorNumber(editAddress.floorNumber || "");
      setApartmentNumber(editAddress.apartmentNumber || "");
      setDetails(editAddress.details || "");
      if (editAddress.latitude && editAddress.longitude) {
        setCoords({ lat: editAddress.latitude, lng: editAddress.longitude });
      }
    } else if (open) {
      setStreetName("");
      setBuildingNumber("");
      setFloorNumber("");
      setApartmentNumber("");
      setDetails("");
    }
  }, [open, editAddress]);

  const reverseGeocode = useCallback(
    async (lat: number, lng: number) => {
      setGeocoding(true);
      setCoords({ lat, lng });
      try {
        const { data, error } = await supabase.functions.invoke("reverse-geocode", {
          body: { lat, lng, language, restaurant_id: brand.id },
        });
        if (!error && data && !data.error) {
          const result = data as GeoResult;
          setGeoResult(result);
          if (result.street && !streetName) setStreetName(result.street);
          if (result.building && !buildingNumber) setBuildingNumber(result.building);
        }
      } catch {
        // silently fail
      } finally {
        setGeocoding(false);
      }
    },
    [language, brand.id, streetName, buildingNumber],
  );

  // Init map when dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const initCenter =
      editAddress?.latitude && editAddress?.longitude
        ? { lat: editAddress.latitude, lng: editAddress.longitude }
        : customer?.lastCoordinates?.latitude && customer?.lastCoordinates?.longitude
          ? { lat: customer.lastCoordinates.latitude, lng: customer.lastCoordinates.longitude }
          : DEFAULT_CENTER;

    // Small delay to ensure the dialog DOM is rendered
    const timeout = setTimeout(() => {
      loadGoogleMaps()
        .then(() => {
          if (cancelled || !mapRef.current) return;
          const map = new (window as any).google.maps.Map(mapRef.current, {
            center: initCenter,
            zoom: 15,
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: "greedy",
            styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }],
          });
          mapInstanceRef.current = map;

          const marker = new (window as any).google.maps.Marker({
            position: initCenter,
            map,
            draggable: true,
            animation: (window as any).google.maps.Animation.DROP,
          });
          markerRef.current = marker;

          marker.addListener("dragend", () => {
            const pos = marker.getPosition();
            if (pos) reverseGeocode(pos.lat(), pos.lng());
          });

          map.addListener("click", (e: any) => {
            if (e.latLng) {
              marker.setPosition(e.latLng);
              reverseGeocode(e.latLng.lat(), e.latLng.lng());
            }
          });

          setLoading(false);
          reverseGeocode(initCenter.lat, initCenter.lng);
        })
        .catch(() => {
          if (!cancelled) setLoading(false);
        });
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      setLoading(true);
      setGeoResult(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      mapInstanceRef.current?.panTo(c);
      markerRef.current?.setPosition(c);
      reverseGeocode(c.lat, c.lng);
    });
  };

  const handleSave = () => {
    const addressData: AddressData = {
      latitude: coords.lat,
      longitude: coords.lng,
      streetName: streetName.trim() || null,
      buildingNumber: buildingNumber.trim() || null,
      floorNumber: floorNumber.trim() || null,
      apartmentNumber: apartmentNumber.trim() || null,
      geocodeAddress: geoResult?.formatted_address || null,
      details: details.trim() || null,
    };
    onSave(addressData);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        <DialogHeader className="p-4 border-b border-border shrink-0">
          <DialogTitle>
            {editAddress
              ? isAr ? "تعديل العنوان" : "Edit Address"
              : isAr ? "إضافة عنوان جديد" : "Add New Address"}
          </DialogTitle>
        </DialogHeader>

        {/* Map */}
        <div className="relative h-48 shrink-0">
          <div ref={mapRef} className="w-full h-full" />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          <button
            onClick={handleMyLocation}
            className="absolute bottom-2 right-2 z-10 bg-background rounded-full p-2 shadow-md border border-border hover:bg-muted transition-colors"
            type="button"
          >
            <MapPin className="h-4 w-4 text-primary" />
          </button>
        </div>

        {/* Geocoded address preview */}
        <div className="px-4 pt-3 shrink-0">
          {geocoding ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {isAr ? "جاري تحديد الموقع..." : "Detecting address..."}
            </div>
          ) : geoResult ? (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {geoResult.formatted_address}
            </p>
          ) : null}
        </div>

        {/* Form */}
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{isAr ? "اسم الشارع" : "Street Name"}</Label>
              <Input
                value={streetName}
                onChange={(e) => setStreetName(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{isAr ? "رقم المبنى" : "Building No."}</Label>
              <Input
                value={buildingNumber}
                onChange={(e) => setBuildingNumber(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{isAr ? "الطابق" : "Floor"}</Label>
              <Input
                value={floorNumber}
                onChange={(e) => setFloorNumber(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{isAr ? "رقم الشقة" : "Apartment No."}</Label>
              <Input
                value={apartmentNumber}
                onChange={(e) => setApartmentNumber(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">{isAr ? "ملاحظات التوصيل" : "Delivery Notes"}</Label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={2}
              className="text-sm resize-none"
              placeholder={isAr ? "مثال: بجانب المسجد..." : "e.g. Near the mosque..."}
            />
          </div>
        </div>

        {/* Save button */}
        <div className="p-4 border-t border-border shrink-0">
          <Button className="w-full" onClick={handleSave} disabled={geocoding || !coords.lat}>
            {isAr ? "حفظ العنوان" : "Save Address"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
